import React, { ReactElement, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Text } from '@shootsta/common-react';
import Intro from './Intro';
import Footer from './Footer';
import QuoteBlock from './QuoteBlock';
import BrandingBlock from './BrandingBlock';
import TestimonialsBlock from './TestimonialsBlock';
import { useShareablePageContext } from '../contexts/ShareablePageContext';
import { usePublicAssetContext } from '../contexts/PublicAssetContext';

import '../styles/_shareable-page.scss';

export default function PageContent(): ReactElement | null {
  const { shareablePage } = useShareablePageContext();
  const { isPublic, error } = usePublicAssetContext();

  const errorMessage = useMemo((): string => {
    if (!isPublic) {
      return 'This content is not public. Please contact the creator to see it.';
    }
    if (error) {
      return 'There was an error retrieving this content :(';
    }

    return '';
  }, [error, isPublic]);

  const {
    shareablePageId,
    assetId,
    logoUrl,
    title,
    brandingColor,
    quote,
    quoteSource,
    quoteBackground,
    testimonialTitle,
    testimonialDescription,
    hypeReelUrl,
    testimonials,
    contactUser
  } = shareablePage!;

  const pageTitle: string = isPublic ? title : 'Shootsta: Private video';
  const showTestimonials = testimonialTitle && testimonialDescription && hypeReelUrl;

  return (
    <div className="splash-page">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <header className="splash-page__header">
        <img src={logoUrl} alt="Company logo" className="splash-page__header__logo" />
      </header>
      <main>
        {errorMessage && (
          <Text className="splash-page__error" centered heading3>
            {errorMessage}
          </Text>
        )}
        {isPublic && (
          <>
            <Intro
              shareablePageId={shareablePageId}
              assetId={assetId}
            />
            {quote && (
              <QuoteBlock
                quote={quote}
                source={quoteSource}
                quoteBackground={quoteBackground}
              />
            )}
            {showTestimonials && (
              <>
                <TestimonialsBlock
                  title={testimonialTitle}
                  description={testimonialDescription}
                  hypeReelUrl={hypeReelUrl}
                  testimonials={testimonials}
                  brandingColor={brandingColor}
                />
                <BrandingBlock
                  brandingColor={brandingColor}
                  shareablePageId={shareablePageId}
                />
              </>
            )}
          </>
        )}
      </main>
      <Footer contactUser={contactUser} />
    </div>
  );
}
